// i18n
import { jaLocale, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { mediaMax, fontWeight, spacing } from 'presentations/views/styles';
import {
  pricingPath,
  featureListPath,
  downloadsPath,
  productIntegrationsPath,
  newsListPath,
  aboutPath,
  helpCenterPath,
  resourceListPath,
  careersHomePath,
  engineeringListPath,
  productSecurityPath,
  customerStoryListPath,
  changelogListPath,
} from 'presentations/routes/path';
import Link from 'presentations/views/components/atoms/Link';
import Image from 'presentations/views/components/atoms/Image';
import { black, gray01 } from 'presentations/views/styles/color';
import { memo, SyntheticEvent, useCallback } from 'react';
import { isInElectron } from 'presentations/views/helpers/electron';
import LanguageSelect from 'presentations/views/components/molecules/LanguageSelect';
import { useRouter } from 'next/router';
import * as tracker from 'presentations/views/helpers/analytics';
import {
  developerPath,
  systemStatusPath,
} from 'presentations/routes/path/developer';

interface Props {
  isHidden?: boolean;
  isThin?: boolean;
}

const Component = memo((props: Props) => {
  if (isInElectron()) return null;
  if (props.isHidden) return null;
  const t = translateInComponent(translations);
  const router = useRouter();
  const locale = router.locale;
  const onChangeLocale = useCallback(
    (e: SyntheticEvent<HTMLInputElement>) => {
      router.push(router.asPath, undefined, { locale: e.currentTarget.value });
    },
    [router],
  );
  const onClickSocial = () => {
    tracker.setEvent('click sns account', {
      position: 'resource sidebar',
    });
  };

  return (
    <Container>
      <ContentFooter>
        <LinkList>
          <LinkListTitle>{t('Product')}</LinkListTitle>
          <List>
            <ListItem>
              <ListItemLink href={featureListPath()}>
                {t('Features')}
              </ListItemLink>
            </ListItem>
            <ListItem>
              <ListItemLink href={downloadsPath()}>
                {t('Downloads')}
              </ListItemLink>
            </ListItem>
            <ListItem>
              <ListItemLink href={productIntegrationsPath()}>
                {t('Integrations')}
              </ListItemLink>
            </ListItem>
            <ListItem>
              <ListItemLink href={productSecurityPath()}>
                {t('Security')}
              </ListItemLink>
            </ListItem>
            <ListItem>
              <ListItemLink href={pricingPath()}>{t('Pricing')}</ListItemLink>
            </ListItem>
          </List>
        </LinkList>
        <LinkList>
          <LinkListTitle>{t('Resources')}</LinkListTitle>
          <List>
            <ListItem>
              <ListItemLink href={newsListPath()}>{t('News')}</ListItemLink>
            </ListItem>
            <ListItem>
              <ListItemLink href={customerStoryListPath()}>
                {t('Customer Stories')}
              </ListItemLink>
            </ListItem>
            <ListItem>
              <ListItemLink href={changelogListPath()}>
                {t('Changelog')}
              </ListItemLink>
            </ListItem>
            <ListItem>
              <ListItemLink href={resourceListPath()}>
                {t('Magazine')}
              </ListItemLink>
            </ListItem>
            <ListItem>
              <ListItemLink href={helpCenterPath()}>{t('Help')}</ListItemLink>
            </ListItem>
          </List>
          <LinkListTitle>{t('Developers')}</LinkListTitle>
          <List>
            <ListItem>
              <ListItemLink href={developerPath()}>
                {t('Developer Center')}
              </ListItemLink>
            </ListItem>
            <ListItem>
              <ListItemLink href={systemStatusPath()}>
                {t('Status')}
              </ListItemLink>
            </ListItem>
          </List>
        </LinkList>
        <LinkList>
          <LinkListTitle>{t('Corporate')}</LinkListTitle>
          <List>
            <ListItem>
              <ListItemLink href={aboutPath()}>{t('About Jicoo')}</ListItemLink>
            </ListItem>
            <ListItem>
              <ListItemLink href={careersHomePath()}>
                {t('Career')}
              </ListItemLink>
            </ListItem>
            <ListItem>
              <ListItemLink href={engineeringListPath()}>
                {t('Engineering')}
              </ListItemLink>
            </ListItem>
          </List>
        </LinkList>
        <LinkList>
          <LinkListTitle>{t('Settings')}</LinkListTitle>
          <ListItem>
            <LanguageSelect onChangeLocale={onChangeLocale} />
          </ListItem>
          {router.locale === jaLocale && (
            <>
              <LinkListTitle>{t('SNS')}</LinkListTitle>
              <ListItem>
                <SocialLink
                  href="https://twitter.com/jicoohq"
                  target="_blank"
                  rel="noopener"
                  onClick={onClickSocial}
                >
                  <SocialLogo
                    src={'mails/logo_twitter.png'}
                    alt={t('Twitter')}
                  />
                </SocialLink>
                <SocialLink
                  href="https://www.facebook.com/jicoohq"
                  target="_blank"
                  rel="noopener"
                  onClick={onClickSocial}
                >
                  <SocialLogo
                    src={'mails/logo_facebook.png'}
                    alt={t('Facebook')}
                  />
                </SocialLink>
              </ListItem>
            </>
          )}
        </LinkList>
      </ContentFooter>
    </Container>
  );
});

const Container = styled.div`
  width: 100%;
  max-width: 960px;
`;
const ContentFooter = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${spacing(16)};
  padding: ${spacing(24)};
  ${mediaMax.tablet} {
    grid-template-columns: repeat(2, 1fr);
    padding: 0 ${spacing(12)};
  }
`;

const LinkList = styled.div``;
const LinkListTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(16)};
  margin-top: ${spacing(24)};
  :first-of-type {
    margin-top: 0;
  }
`;

const List = styled.div``;
const ListItem = styled.div`
  margin-bottom: ${spacing(8)};
`;
const ListItemLink = styled(Link)`
  color: ${gray01};
  :hover {
    color: ${black};
  }
`;
const SocialLogo = styled(Image)`
  width: 32px;
  height: 32px;
`;
const SocialLink = styled(Link)`
  margin-right: ${spacing(16)};
  :last-of-type {
    margin-right: 0;
  }
`;
export default Component;
